import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { cn } from "~/lib/utils";

export type AiAgentEmailSignatureProps = {
  agentName: string;
  agentTitle: string;
  companyName: string;
  avatarSrc?: string | null;
  headerColor?: string;
  phoneNumber?: string | null;
  emailAddress?: string | null;
  websiteUrl?: string | null;
  className?: string;
};

function hasValidPhoneNumber(phoneNumber?: string | null): boolean {
  const rawPhone = (phoneNumber ?? "").trim();
  const digitsOnly = rawPhone.replace(/\D+/g, "");
  return rawPhone.length > 0 && digitsOnly.length >= 7;
}

function formatWebsiteDisplay(url: string): string {
  return url.replace(/^https?:\/\//i, "").replace(/\/$/, "");
}

function normalizeWebsiteHref(url: string): string {
  return url.startsWith("http") ? url : `https://${url}`;
}

export function AiAgentEmailSignature({
  agentName,
  agentTitle,
  companyName,
  avatarSrc,
  headerColor = "#D9AC48",
  phoneNumber,
  emailAddress,
  websiteUrl,
  className,
}: AiAgentEmailSignatureProps) {
  const initials = agentName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "AI";

  const hasPhone = hasValidPhoneNumber(phoneNumber);
  const website = websiteUrl?.trim() ?? "";

  return (
    <div className={cn("flex items-start gap-3", className)}>
      <Avatar className="h-[70px] w-[70px] shrink-0 overflow-hidden">
        {avatarSrc ? (
          <AvatarImage src={avatarSrc} alt={agentName} className="object-cover" />
        ) : null}
        <AvatarFallback className="text-base font-semibold">{initials}</AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1 space-y-0.5 text-xs leading-snug text-foreground">
        <p className="text-base font-semibold leading-tight" style={{ color: headerColor }}>
          {agentName}
        </p>
        <p className="text-xs text-foreground">{agentTitle}</p>
        <p className="text-sm font-bold text-foreground">{companyName}</p>

        <div className="space-y-0.5 pt-1.5">
          <p>
            <span className="font-bold" style={{ color: headerColor }}>
              Direct Line:
            </span>{" "}
            {hasPhone ? (
              <>
                <a
                  href={`tel:${phoneNumber}`}
                  className="text-foreground no-underline hover:underline"
                >
                  {phoneNumber}
                </a>{" "}
                <span className="text-muted-foreground">(AI Receptionist Enabled)</span>
              </>
            ) : (
              <span className="text-muted-foreground">AI Receptionist Disabled</span>
            )}
          </p>

          {emailAddress ? (
            <p>
              <span className="font-bold" style={{ color: headerColor }}>
                Email:
              </span>{" "}
              <a
                href={`mailto:${emailAddress}`}
                className="break-all text-foreground no-underline hover:underline"
              >
                {emailAddress}
              </a>
            </p>
          ) : null}

          {website ? (
            <p>
              <span className="font-bold" style={{ color: headerColor }}>
                Website:
              </span>{" "}
              <a
                href={normalizeWebsiteHref(website)}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-foreground no-underline hover:underline"
              >
                {formatWebsiteDisplay(website)}
              </a>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
